import { useEffect, useState } from 'react';
import { Float, Html, Image, Text, Text3D, Center } from "@react-three/drei";
import { animated, useSpring } from "@react-spring/three";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import layout from './dictionaries/layout';
import translations from './dictionaries/translations';
import RocketAnimated from './meshes/RocketAnimated';
import UfoAnimated from './meshes/UfoAnimated';
import { useLocation } from 'wouter';
import HowToPlay from './components/HowToPlay';
import { getSocket } from './socket.js';

import { audioVolumeAtom, blueMoonBrightnessAtom, clientAtom, deviceAtom, languageAtom, languageModalOpenAtom } from './GlobalState';
import Board from './components/Board';
import GameCamera from './sceneSetUp/GameCamera';
import Rocket from './meshes/Rocket';
import Ufo from './meshes/Ufo';
import useResponsiveSetting from './hooks/useResponsiveSetting';
import MeteorsRealShader from './shader/meteorsReal/MeteorsRealShader';
import YootDisplay from './components/YootDisplay';
import DisconnectModal from './components/DisconnectModal';
import * as THREE from 'three';
import useSoundEffectsPlayer from './soundPlayers/useSoundEffectsPlayer';
import useMusicPlayer from './soundPlayers/useMusicPlayer';
import MilkyWayNew from './shader/milkyway/MilkyWayNew';
import { trackEvent } from './api';
import About from './components/About';
import StarsPatterns2Shader from './shader/starsPatterns2/StarsPatterns2Shader';
import Constellation from './shader/constellation/Constellation';
import Showroom from './components/Showroom';
import { RHINOLogo } from './meshes/RHINOLogo.jsx';
import Earth from './meshes/Earth.jsx';
import EarthButton from './meshes/EarthButton.jsx';
import DiscordLogo from './meshes/DiscordLogo.jsx';
import MeshColors from './components/MeshColors.jsx';
import LanguageModalAnimated from './components/lobby/LanguageModalAnimated.jsx';

export default function Home2({ showRulebookDefault = false, showAboutDefault = false }) {

  useResponsiveSetting();
  const device = useAtomValue(deviceAtom)
  const [display, setDisplay] = useState(showRulebookDefault ? 'howToPlay' : showAboutDefault ? 'about' : 'title')
  const client = useAtomValue(clientAtom)
  const setBlueMoonBrightness = useSetAtom(blueMoonBrightnessAtom)
  const [_location, setLocation] = useLocation();
  const { playSoundEffect } = useSoundEffectsPlayer()
  const { loopMusic } = useMusicPlayer()
  const setAudioVolume = useSetAtom(audioVolumeAtom)
  const language = useAtomValue(languageAtom)
  const languageModalOpen = useAtomValue(languageModalOpenAtom)
  const setLanguageModalOpen = useSetAtom(languageModalOpenAtom)
  useEffect(() => {
    trackEvent({ surface: 'home', action: 'page_view', category: 'page_view' });
  }, [])
  
  // games played, page visits, yut thrown
  // stats page button
  function StatsLandscape() {
    return <group>
      <Text3D
        font="/fonts/Luckiest Guy_Regular.json"
        position={position}
        rotation={rotation}
        size={0.3}
        height={0.01}
        color='limegreen'
      >
        {displayString}
        <meshStandardMaterial color='orange'/>
      </Text3D>
    </group>
  }
  function StatsPortrait() {
    return <group>
    </group>
  }

  function Title({ position, rotation, scale }) {

    const [hover, setHover] = useState(false);
    const { playSoundEffect } = useSoundEffectsPlayer()
    const textFont = language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"
    const textSize = language === 'korean' ? 0.35 : 0.4

    function handlePointerEnter() {
        setHover(true)
    }
    function handlePointerLeave() {
        setHover(false)
    }
    function handlePointerDown(e) {
      e.stopPropagation()
      setDisplay('title')
      playSoundEffect('/sounds/effects/button-click.mp3', 1)
    }

    return <group scale={scale} position={position} rotation={rotation}>
      {/* line 1 */}
      {/* line 2 */}
      {/* wrapper */}
      {language === 'chinese' ? <Text3D
        font={textFont}
        size={textSize}
        height={0.01}
        position={[0, -0.75, 0]}
        letterSpacing={0.04}
      >
        {`${translations.homeTitle.line1[language]}${translations.homeTitle.line2[language]}`}
        <meshStandardMaterial color={hover ? "green": 'orange'} />
      </Text3D> : <>
        <Text3D
          font={textFont}
          size={textSize}
          height={0.01}
          position={[0, -0.5, 0]}
          letterSpacing={0.04}
        >
          {translations.homeTitle.line1[language]}
          <meshStandardMaterial color={hover ? "green": 'orange'} />
        </Text3D>
        <Text3D
          font={textFont}
          size={textSize}
          height={0.01}
          position={[0, -1, 0]}
          letterSpacing={0.03}
        >
          {translations.homeTitle.line2[language]}
          <meshStandardMaterial color={hover ? "green": 'orange'} />
        </Text3D>
      </>}
      <mesh 
        position={[0.65, -0.5, -0.1]} 
        scale={[1, 0.01, 2]}
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
        onPointerDown={e=>handlePointerDown(e)}
      >
        <boxGeometry args={[1.5, 0.01, 1.2]}/>
        <meshStandardMaterial color="orange" transparent opacity={0}/>
      </mesh>
    </group>
  }

  function Pieces() {
    return <group>
      {/* rocket on star 1 */}
      <Float 
        speed={3} 
        rotationIntensity={0.2}
        floatIntensity={0.1}
        floatingRange={[-0.1, 0.1]}
      >
        <RocketAnimated position={[1.7,0.5,4.3]} rotation={[-Math.PI/4, 0, 0]} scale={0.4}/>
      </Float>
      {/* rocket on moon */}
      <Float 
        speed={3} 
        rotationIntensity={0.3}
        floatIntensity={0.1}
        floatingRange={[-1, 1]}
      >
        <RocketAnimated position={[0,2,-0.1]} rotation={[-Math.PI/4, 0, 0]} scale={0.4}/>
      </Float>
      {/* rocket home pieces */}
      <Rocket 
        position={layout[device].title.pieces.rocketHome0.position} 
        rotation={layout[device].title.pieces.rocketHome0.rotation} 
        scale={layout[device].title.pieces.rocketHome0.scale}
      />
      <Rocket 
        position={layout[device].title.pieces.rocketHome1.position} 
        rotation={layout[device].title.pieces.rocketHome1.rotation} 
        scale={layout[device].title.pieces.rocketHome1.scale}
      />
      <mesh name='rocket-home' scale={layout[device].title.rocketHome.scale} position={layout[device].title.rocketHome.position}>
        <cylinderGeometry args={[1, 1, 1]}/>
        <meshStandardMaterial color='red' transparent opacity={0.1}/>
      </mesh>
      {/* ufo home piece */}
      <Ufo 
        rotation={layout[device].title.pieces.ufoHome.rotation} 
        position={layout[device].title.pieces.ufoHome.position} 
        scale={layout[device].title.pieces.ufoHome.scale}
      />
      <mesh name='ufo-home' scale={layout[device].title.ufoHome.scale} position={layout[device].title.ufoHome.position}>
        <cylinderGeometry args={[1, 1, 1]}/>
        <meshStandardMaterial color='turquoise' transparent opacity={0.1}/>
      </mesh>
      {/* ufo on mars */}
      <Float 
        rotationIntensity={0.05} 
        speed={4} 
        floatIntensity={1}
        floatingRange={[0, 0.2]}
      >
        <Ufo rotation={[0,0,0]} position={[5.0, 0.8, -0.5]} scale={1} onBoard/>
      </Float>
      {/* ufo on saturn */}
      <Float rotationIntensity={0.03} speed={3} floatIntensity={1} floatingRange={[-0.2, 0.2]}>
        <Ufo rotation={[0,0,0]} position={[-3.3, 0.8, -4.1]} scale={1} onBoard/>
        <Ufo rotation={[0,0,0]} position={[-2.6, 0.8, -4.1]} scale={1} onBoard/>
      </Float>
    </group>
  }

  function HowToPlayButton({ position, rotation, scale }) {
    const [hover, setHover] = useState(false)

    function handlePointerEnter(e) {
      e.stopPropagation();
        document.body.style.cursor = 'pointer'
      setHover(true)
    }

    function handlePointerLeave(e) {
      e.stopPropagation();
      document.body.style.cursor = 'default'
      setHover(false)
    }

    async function handlePointerDown(e) {
      e.stopPropagation();
      setLocation(`/how-to-play`)

      playSoundEffect('/sounds/effects/button-click.mp3', 1)

      await trackEvent({ surface: 'home', action: 'open_how_to_play', category: 'ui' })
    }

    const textContent = translations.howToPlay[language]
    const textFont = language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"

    return <group position={position} rotation={rotation} scale={scale}>
      <mesh>
        <boxGeometry args={[3.15, 0.03, 0.55]}/>
        <meshStandardMaterial color={ hover || display === 'howToPlay' ? 'green': 'orange'}/>
      </mesh>
      <mesh>
        <boxGeometry args={[3.1, 0.04, 0.5]}/>
        <meshStandardMaterial color='black'/>
      </mesh>
      <mesh 
        name='wrapper' 
        onPointerEnter={e => handlePointerEnter(e)}
        onPointerLeave={e => handlePointerLeave(e)}
        onPointerDown={e => handlePointerDown(e)}
      >
        <boxGeometry args={[3.15, 0.1, 0.55]}/>
        <meshStandardMaterial transparent opacity={0}/>
      </mesh>
      <Center position={[0, 0.05, 0]} rotation={[-Math.PI/2, 0, 0]}>
        <Text3D
          font={textFont}
          size={0.3}
          height={0.01}
        >
          {textContent}
          <meshStandardMaterial color={ hover || display === 'howToPlay' ? 'green': 'orange'}/>
        </Text3D>
      </Center>
    </group>
  }

  function LetsPlayButton({ position, rotation, scale }) {
    const [hover, setHover] = useState(false)

    function handlePointerEnter(e) {
      e.stopPropagation();
        document.body.style.cursor = 'pointer'
      setHover(true)
    }

    function handlePointerLeave(e) {
      e.stopPropagation();
        document.body.style.cursor = 'default'
      setHover(false)
    }

    const [isThrottled, setIsThrottled] = useState(false)
    async function handlePointerUp(e) {
      e.stopPropagation();
      if (isThrottled) return
      
      setIsThrottled(true)

      setTimeout(() => {
        setIsThrottled(false)
      }, 1000)

      // Get fresh socket instance
      const socket = getSocket();

      // Connect to socket if not already connected
      if (!socket.connected) {
        socket.connect()
      }

      // Wait for connection before creating room
      const createRoom = () => {
        socket.emit('createRoom', { hostId: client._id }, ({ shortId }) => {
          setLocation(`/game/${shortId}`)
        })

        // this doesn't play
        // music doesn't play either
        playSoundEffect('/sounds/effects/create-game.mp3', 1)
        loopMusic(1, true)
        setAudioVolume(1) // use this in the next music play
        
        setBlueMoonBrightness(null)
      }

      if (socket.connected) {
        createRoom()
      } else {
        socket.once('connect', createRoom)
      }

      await trackEvent({ surface: 'home', action: 'host_game', category: 'gameplay' })
    }

    const textContent = translations.hostAGame[language]
    const textFont = language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"

    return <group position={position} rotation={rotation} scale={scale}>
      <mesh name='background-outer'>
        <boxGeometry args={[3.15, 0.03, 0.55]}/>
        <meshStandardMaterial color={ hover ? 'green': 'orange'}/>
      </mesh>
      <mesh name='background-inner'>
        <boxGeometry args={[3.1, 0.04, 0.5]}/>
        <meshStandardMaterial color='black'/>
      </mesh>
      <mesh 
        name='wrapper' 
        onPointerEnter={e => handlePointerEnter(e)}
        onPointerLeave={e => handlePointerLeave(e)}
        onPointerUp={e => handlePointerUp(e)}
      >
        <boxGeometry args={[3.15, 0.1, 0.55]}/>
        <meshStandardMaterial transparent opacity={0}/>
      </mesh>
      <Center position={[0, 0.05, 0]} rotation={[-Math.PI/2, 0, 0]}>
        <Text3D
          font={textFont}
          size={0.3}
          height={0.01}
        >
          {textContent}
          <meshStandardMaterial color={ hover ? 'green': 'orange'}/>
        </Text3D>
      </Center>
    </group>
  }

  function JoinGameButton({ position, rotation, scale }) {
    const [hover, setHover] = useState(false)

    function handlePointerEnter(e) {
      e.stopPropagation();
      document.body.style.cursor = 'pointer'
      setHover(true)
    }

    function handlePointerLeave(e) {
      e.stopPropagation();
      document.body.style.cursor = 'default'
      setHover(false)
    }

    function handlePointerDown(e) {
      e.stopPropagation();

      playSoundEffect('/sounds/effects/button-click.mp3', 1)
      setLanguageModalOpen(false)
      setDisplay('joinGameModal')
      trackEvent({ surface: 'home', action: 'open_join_game', category: 'ui' })
      
    }

    const textContent = translations.joinAGame[language]
    const textFont = language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"

    return <group position={position} rotation={rotation} scale={scale}>
      <mesh name='background-outer'>
        <boxGeometry args={[3.15, 0.03, 0.55]}/>
        <meshStandardMaterial color={ hover ? 'green': 'orange'}/>
      </mesh>
      <mesh name='background-inner'>
        <boxGeometry args={[3.1, 0.04, 0.5]}/>
        <meshStandardMaterial color='black'/>
      </mesh>
      <mesh 
        name='wrapper' 
        onPointerEnter={e => handlePointerEnter(e)}
        onPointerLeave={e => handlePointerLeave(e)}
        onPointerDown={e => handlePointerDown(e)}
      >
        <boxGeometry args={[3.15, 0.1, 0.55]}/>
        <meshStandardMaterial transparent opacity={0}/>
      </mesh>
      <Center position={[0, 0.05, 0]} rotation={[-Math.PI/2, 0, 0]}>
        <Text3D
          font={textFont}
          size={0.3}
          height={0.01}
        >
          {textContent}
          <meshStandardMaterial color={ hover ? 'green': 'orange'}/>
        </Text3D>
      </Center>
    </group>
  }

  function DiscordButton({ position, rotation, scale }) {
    const [hover, setHover] = useState(false)

    function handlePointerEnter(e) {
      e.stopPropagation()
      document.body.style.cursor = 'pointer'
      setHover(true)
    }

    function handlePointerLeave(e) {
      e.stopPropagation()
      document.body.style.cursor = 'default'
      setHover(false)
    }

    function handlePointerUp(e) {
      e.stopPropagation()
      window.open('https://discord.gg/RwbTfezXu7', '_blank', 'noreferrer')
      trackEvent({ surface: 'home', action: 'open_discord', category: 'ui' })
    }

    return <group position={position} rotation={rotation} scale={scale}>
      <mesh
        name='discord-button-wrapper'
        position={[0, -0.02, 0]}
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
        onPointerUp={handlePointerUp}
      >
        <boxGeometry args={[0.8, 0.1, 0.8]}/>
        <meshStandardMaterial color='yellow' transparent opacity={0}/>
      </mesh>
      <Center position={[0, 0.05, 0]} rotation={[-Math.PI/2, 0, 0]}>
        <DiscordLogo position={[0, 0.12, 0]} scale={0.28} color={hover ? 'green' : 'yellow'}/>
      </Center>
    </group>
  }

  function JoinGameModal({ position, rotation, scale }) {
    const [roomId, setRoomId] = useState('')
    const [alert, setAlert] = useState('')
    const [submitHover, setSubmitHover] = useState(false)
    const [cancelHover, setCancelHover] = useState(false)
    const modalTranslations = translations.joinGameModal
    
    function isAlphaNumeric(str) {
      for (let i = 0; i < str.length; i++) {
        let code = str.charCodeAt(i);
        if (!(code > 47 && code < 58) && // numeric (0-9)
            !(code > 64 && code < 91) && // upper alpha (A-Z)
            !(code > 96 && code < 123) && // lower alpha (a-z)
            !(code === 32)) { // whitespace
          return false;
        }
      }
      return true;
    };

    function handleJoinSubmit(e) {
      e.preventDefault();
      if (isAlphaNumeric(roomId)) {
        // Get fresh socket instance
        const socket = getSocket();

        // Connect to socket if not already connected
        if (!socket.connected) {
          socket.connect()
        }

        const checkAndJoin = () => {
          socket.emit('checkRoomExists', { roomId: roomId.toUpperCase() }, ({ exists }) => {
            if (exists) {
              trackEvent({ surface: 'home', action: 'submit_join_game', category: 'gameplay', metadata: { result: 'found' } })
              setLocation(`/game/${roomId.toUpperCase()}`)
            } else {
              trackEvent({ surface: 'home', action: 'submit_join_game', category: 'gameplay', metadata: { result: 'not_found' } })
              setAlert(modalTranslations.errorNotExists[language])
            }
          })
        }

        if (socket.connected) {
          checkAndJoin()
        } else {
          socket.once('connect', checkAndJoin)
        }
      } else {
        trackEvent({ surface: 'home', action: 'submit_join_game', category: 'gameplay', metadata: { result: 'invalid' } })
        setAlert(modalTranslations.errorAlphaNumeric[language])
      }
    }
    
    function handleJoinCancel(e) {
      e.preventDefault()
      setDisplay('title')
      setAlert('');
      trackEvent({ surface: 'home', action: 'cancel_join_game', category: 'ui' })
    }

    function handleSubmitMouseEnter () {
      setSubmitHover(true)
    }
    function handleSubmitMouseLeave () {
      setSubmitHover(false)
    }
    function handleCancelMouseEnter () {
      setCancelHover(true)
    }
    function handleCancelMouseLeave () {
      setCancelHover(false)
    }

    const modalFont = language === 'korean'
      ? 'MaplestoryLight'
      : language === 'chinese'
        ? 'XiaoXiangjiao'
        : 'Luckiest Guy'
    
    return <group 
      position={position}
      rotation={rotation}
      scale={scale}
    >
      <Html transform>
        <div style={{ position: 'absolute' }}>
          <form onSubmit={e => handleJoinSubmit(e)}>
            <div style={{
              top: '40%',
              width: 'min(220px, 70vw)',
              boxSizing: 'border-box',
              backgroundColor: 'black',
              border: '2px solid #F1EE92',
              padding: '10px'
            }}>
              <p style={{
                fontFamily: modalFont,
                color: '#F1EE92',
                fontSize: '15px',
                padding: '5px',
                margin: '0px',
                textAlign: 'center',
                letterSpacing: '0.6px',
                overflowWrap: 'anywhere'
              }}>
                {modalTranslations.title[language]}
              </p>
              <input
                style={{
                  width: 'min(180px, 100%)',
                  display: 'block',
                  margin: '0 auto',
                  boxSizing: 'border-box',
                  background: 'none',
                  fontFamily: modalFont,
                  fontSize: `15px`,
                  color: '#F1EE92',
                  padding: '5px',
                  border: '2px solid #F1EE92'
                }}
                onChange={e => setRoomId(e.target.value)}
                placeholder={modalTranslations.placeholder[language]}/>
              <div>
                <p style={{ margin: '5px', color: 'red', fontFamily: modalFont, fontSize: '10px' }}>
                  {alert}
                </p>
              </div>
              <div style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                flexWrap: 'wrap'
              }}>
                <button 
                  id='join-team-submit-button'
                  style={{
                    fontFamily: modalFont,
                    fontSize: `15px`,
                    background: 'none',
                    border: `2px solid ${submitHover ? 'white' : '#F1EE92'}`,
                    margin: '4px',
                    padding: '4px',
                    color: `${submitHover ? 'white' : '#F1EE92'}`,
                    position: 'relative',
                    letterSpacing: '0.6px'
                  }}
                  onMouseOver={handleSubmitMouseEnter}
                  onMouseOut={handleSubmitMouseLeave}
                  type="submit">
                {modalTranslations.submit[language]}
                </button>
                {/* highlight on hover */}
                <button 
                  id='join-team-cancel-button'
                  style={{
                    fontFamily: modalFont,
                    fontSize: `15px`,
                    background: 'none',
                    border: `2px solid ${cancelHover ? 'white' : 'red'}`,
                    margin: '4px',
                    padding: '4px',
                    color: `${cancelHover ? 'white' : 'red'}`,
                  }}
                  onMouseOver={handleCancelMouseEnter}
                  onMouseOut={handleCancelMouseLeave}
                  onMouseDown={e => handleJoinCancel(e)}
                  // need this to stop form from submitting
                  type="button">
                {modalTranslations.cancel[language]}
                </button>
              </div>
            </div>
          </form>
        </div>
      </Html>
    </group>
  }

  function ShowroomButtonLandscape(props) {
    const [hover, setHover] = useState(false)

    function handlePointerEnter(e) {
      e.stopPropagation();
      document.body.style.cursor = 'pointer'
      setHover(true)
    }

    function handlePointerLeave(e) {
      e.stopPropagation();
      document.body.style.cursor = 'default'
      setHover(false)
    }

    function handlePointerDown(e) {
      e.stopPropagation();

      playSoundEffect('/sounds/effects/button-click.mp3', 1)
      
      setDisplay('showroom')
    }

    const textContent = translations.showroom[language]
    const textFont = language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"

    return <group {...props}>
      <mesh name='background-outer'>
        <boxGeometry args={[2.55, 0.03, 0.55]}/>
        <meshStandardMaterial color={ hover ? 'green': 'orange'}/>
      </mesh>
      <mesh name='background-inner'>
        <boxGeometry args={[2.5, 0.04, 0.5]}/>
        <meshStandardMaterial color='black'/>
      </mesh>
      <mesh 
        name='wrapper' 
        onPointerEnter={e => handlePointerEnter(e)}
        onPointerLeave={e => handlePointerLeave(e)}
        onPointerDown={e => handlePointerDown(e)}
      >
        <boxGeometry args={[2.7, 0.1, 0.55]}/>
        <meshStandardMaterial transparent opacity={0}/>
      </mesh>
      <Center position={[0, 0.05, 0]} rotation={[-Math.PI/2, 0, 0]}>
        <Text3D
          font={textFont}
          size={0.3}
          height={0.01}
        >
          {textContent}
          <meshStandardMaterial color={ hover ? 'green': 'orange'}/>
        </Text3D>
      </Center>
    </group>
  }
  function ShowroomButtonPortrait(props) {
    const [hover, setHover] = useState(false)

    function handlePointerEnter(e) {
      e.stopPropagation();
      document.body.style.cursor = 'pointer'
      setHover(true)
    }

    function handlePointerLeave(e) {
      e.stopPropagation();
      document.body.style.cursor = 'default'
      setHover(false)
    }

    function handlePointerDown(e) {
      e.stopPropagation();

      playSoundEffect('/sounds/effects/button-click.mp3', 1)
      
      setDisplay('showroom')
    }

    return <group {...props}>
      {/* <mesh name='background-outer'>
        <boxGeometry args={[2.5, 0.03, 2.1]}/>
        <meshStandardMaterial color={ hover ? 'green': [0.8, 0.8, 0]}/>
      </mesh>
      <mesh name='background-inner'>
        <boxGeometry args={[2.45, 0.04, 2.05]}/>
        <meshStandardMaterial color='black'/>
      </mesh> */}
      <mesh 
        name='wrapper' 
        onPointerEnter={e => handlePointerEnter(e)}
        onPointerLeave={e => handlePointerLeave(e)}
        onPointerDown={e => handlePointerDown(e)}
      >
        <boxGeometry args={[2.5, 0.1, 2.1]}/>
        <meshStandardMaterial transparent opacity={0}/>
      </mesh>
    </group>
  }
  function AboutButtonLandscape(props) {
    const [hover, setHover] = useState(false)

    function handlePointerEnter(e) {
      e.stopPropagation();
      document.body.style.cursor = 'pointer'
      setHover(true)
    }

    function handlePointerLeave(e) {
      e.stopPropagation();
      document.body.style.cursor = 'default'
      setHover(false)
    }

    function handlePointerDown(e) {
      e.stopPropagation();

      playSoundEffect('/sounds/effects/button-click.mp3', 1)
      
      setDisplay('about')
    }

    const textContent = translations.about[language]
    const textFont = language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"

    return <group {...props}>
      <mesh name='background-outer'>
        <boxGeometry args={[1.6, 0.03, 0.55]}/>
        <meshStandardMaterial color={ hover ? 'green': 'orange'}/>
      </mesh>
      <mesh name='background-inner'>
        <boxGeometry args={[1.55, 0.04, 0.5]}/>
        <meshStandardMaterial color='black'/>
      </mesh>
      <mesh 
        name='wrapper' 
        onPointerEnter={e => handlePointerEnter(e)}
        onPointerLeave={e => handlePointerLeave(e)}
        onPointerDown={e => handlePointerDown(e)}
      >
        <boxGeometry args={[1.6, 0.1, 0.55]}/>
        <meshStandardMaterial transparent opacity={0}/>
      </mesh>
      <Center position={[0, 0.05, 0]} rotation={[-Math.PI/2, 0, 0]}>
        <Text3D
          font={textFont}
          size={0.3}
          height={0.01}
        >
          {textContent}
          <meshStandardMaterial color={ hover ? 'green': 'orange'}/>
        </Text3D>
      </Center>
    </group>
  }

  function LanguageButton(props) {
    const [hover, setHover] = useState(false)

    function handlePointerEnter(e) {
      e.stopPropagation();
      document.body.style.cursor = 'pointer'
      setHover(true)
    }

    function handlePointerLeave(e) {
      e.stopPropagation();
      document.body.style.cursor = 'default'
      setHover(false)
    }

    function handlePointerDown(e) {
      e.stopPropagation();
      playSoundEffect('/sounds/effects/button-click.mp3', 1)
      setDisplay('title')
      setLanguageModalOpen(true)
    }

    return <group {...props}>
      <mesh 
        name='wrapper' 
        onPointerEnter={e => handlePointerEnter(e)}
        onPointerLeave={e => handlePointerLeave(e)}
        onPointerDown={e => handlePointerDown(e)}
      >
        <boxGeometry args={[1, 0.1, 0.65]}/>
        <meshStandardMaterial transparent opacity={0}/>
      </mesh>
      <group scale={[0.6, 1, 1]}>
        <Image
          url={ '/images/translate-icon-easy-orange.png' }
          scale={1.4}
          position={[0, 0, 0]}
          rotation={[-Math.PI/16, 0, 0]}
        />
      </group>
    </group>
  }

  function Subtitle({ position, rotation, scale }) {
    const textFont = language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"
    const textContent = translations.subtitle[language]

    return <group position={position} rotation={rotation} scale={scale}>
      <Text3D
        font={textFont}
        size={0.3}
        height={0.01}
        rotation={[-Math.PI/2, 0, 0]}
      >
        {textContent}
        <meshStandardMaterial color={MeshColors.menuGold}/>
      </Text3D>
    </group>
  }

  // To make room in portrait mode
  const { titleScale, titlePosition, titleBoardScale, navigationPosition, showroomScale, milkyWayPosition, milkyWayScale, joinGameModalScale } = useSpring({
    titleScale: display === 'howToPlay' ? 0.5 : 1,
    titlePosition: display === 'howToPlay' ? [-2,0,-5] : [0,0,0],
    yutDisplayScale: display === 'howToPlay' ? 0.5 : 1,
    yutDisplayPosition: display === 'howToPlay' ? [-2,0,-5] : [0,0,0],
    titleBoardScale: display === 'title' && !languageModalOpen ? 1 : 0,
    showroomScale: display === 'showroom' ? 1 : 0,  
    navigationPosition: display === 'showroom' ? [-13,0,0] : [0,0,0],
    milkyWayPosition: display === 'showroom' ? [-4,0,0] : [0,0,0],
    milkyWayScale: display !== 'showroom' ? 1 : 0,
    joinGameModalScale: display === 'joinGameModal' ? 1 : 0,
    config: {
      tension: 170,
      friction: 26
    },
  })
  
  const meteorShaderColor = new THREE.Color();
  meteorShaderColor.setHSL(0.05, 0.7, 0.4)
  return <>
    <GameCamera 
      position={layout[device].title.camera.position}
      lookAt={layout[device].title.camera.lookAt}
    />
    <group name='home' scale={layout[device].title.scale} position={layout[device].title.position}>
      <animated.group name='navigation' position={navigationPosition}>
        { device === 'portrait' && <animated.group scale={titleScale} position={titlePosition}>
          <Title 
            position={layout[device].title.text.position}
            rotation={layout[device].title.text.rotation}
            scale={layout[device].title.text.scale}
            setDisplay={setDisplay}
          />
        </animated.group>}
        { device === 'landscapeDesktop' && <Title 
          position={layout[device].title.text.position}
          rotation={layout[device].title.text.rotation}
          scale={layout[device].title.text.scale}
          setDisplay={setDisplay}
        />}
        { device === 'portrait' && <animated.group scale={titleScale} position={titlePosition}>
          <YootDisplay
            position={layout[device].title.yoots.position}
            rotation={layout[device].title.yoots.rotation}
            scale={layout[device].title.yoots.scale} 
            enableHoverEffect={true}
          />
        </animated.group> }
        { device === 'landscapeDesktop' && <YootDisplay
          position={layout[device].title.yoots.position}
          rotation={layout[device].title.yoots.rotation}
          scale={layout[device].title.yoots.scale} 
          enableHoverEffect={true}
        />}
        <Subtitle
          position={layout[device].title.subtitle.position}
          rotation={layout[device].title.subtitle.rotation}
          scale={layout[device].title.subtitle.scale}
        />
        <HowToPlayButton
          position={layout[device].title.howToPlay.position} 
          rotation={layout[device].title.howToPlay.rotation}
          scale={layout[device].title.howToPlay.scale}
        />
        <JoinGameButton
          position={layout[device].title.joinGame.position} 
          rotation={layout[device].title.joinGame.rotation}
          scale={layout[device].title.joinGame.scale}
        />
        <DiscordButton
          position={layout[device].title.discord.position}
          rotation={layout[device].title.discord.rotation}
          scale={layout[device].title.discord.scale}
        />
        <animated.group scale={joinGameModalScale}>
          <JoinGameModal
            position={layout[device].title.joinGameModal.position} 
            rotation={layout[device].title.joinGameModal.rotation}
            scale={layout[device].title.joinGameModal.scale}
          />
        </animated.group>
        <LetsPlayButton
          position={layout[device].title.letsPlay.position} 
          rotation={layout[device].title.letsPlay.rotation}
          scale={layout[device].title.letsPlay.scale}
        />
        { device === 'landscapeDesktop' && <ShowroomButtonLandscape
          position={layout[device].title.showroom.position} 
          rotation={layout[device].title.showroom.rotation}
          scale={layout[device].title.showroom.scale}
        /> }
        <LanguageButton
          position={layout[device].title.languageButton.position} 
          rotation={layout[device].title.languageButton.rotation}
          scale={layout[device].title.languageButton.scale}
        />
      </animated.group>
      <group name='display'>
        <group position={layout[device].title.board.position} 
          scale={layout[device].title.board.scale}>
          <animated.group scale={titleBoardScale}>
            <Board 
              showStart={true} 
              interactive={false}
              legalTiles={{}} 
              helperTiles={{}}
            />
            <Pieces/>
          </animated.group>
        </group>
        { display === 'showroom' && <animated.group scale={showroomScale}>
          <Showroom
            position={layout[device].showroom.position}
            rotation={layout[device].showroom.rotation}
            scale={layout[device].showroom.scale}
            setHomeDisplay={setDisplay}
          />
        </animated.group> }
        <LanguageModalAnimated
          position={[0, 0.02, 0]}
          rotation={[0, 0, 0]}
          scale={1}
        />
      </group>
      {/* { !connectedToServer && <DisconnectModal
        position={layout[device].title.disconnectModal.position}
        rotation={layout[device].title.disconnectModal.rotation}
      /> } */}
      <MeteorsRealShader color={meteorShaderColor}/>
      <animated.group position={milkyWayPosition} scale={milkyWayScale}>
        <MilkyWayNew
          rotation={[-Math.PI/2, 0, -35.0]} 
          position={[0,-1,0]} 
          scale={4}
          brightness={0.5}
          colorTint1={new THREE.Vector4(0.0, 1.0, 1.0, 1.0)}
          colorTint2={new THREE.Vector4(0.0, 1.0, 1.0, 1.0)}
          colorTint3={new THREE.Vector4(0.0, 1.0, 1.0, 1.0)}
        />
      </animated.group>
      <StarsPatterns2Shader count={10000} texturePath={'/textures/particles/3.png'}/>
      <StarsPatterns2Shader count={15000} texturePath={'/textures/particles/6.png'} size={2}/>
      <Constellation omitFactor={2} position={[-15.5,-1,-6.5]} rotation={[-Math.PI/2, 0, Math.PI/16]} scale={1.3} modelPath={'/models/star.glb'}/>
      <Constellation omitFactor={4} position={[-8.7,-1,-7.1]} rotation={[-Math.PI/2, 0, Math.PI/4]} scale={0.9} modelPath={'/models/star.glb'}/>
      <Constellation omitFactor={2} position={[-15.5,-1,3.5]} rotation={[-Math.PI/2, 0, Math.PI/6]} scale={1.2} modelPath={'/models/star.glb'}/>
      <Constellation omitFactor={2} position={[-8.5,-1,1]} rotation={[-Math.PI/2, 0, Math.PI/4]} scale={1.3} modelPath={'/models/star.glb'}/>
      {/* top right */}
      <Constellation omitFactor={2} position={[4.4,-1,-6.3]} rotation={[-Math.PI/2, 0, Math.PI/4]} scale={1.3} modelPath={'/models/star.glb'}/>  
    </group>
  </>
}
